'use client';

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface BundlePartLine {
    name: string;
    quantity: number;
}

/** Parses the "Name ×qty, Name ×qty" summary stored in a Pick-Pack cart item's `Zestaw` attribute. */
export function parseBundleSummary(summary: string): BundlePartLine[] {
    return summary
        .split(', ')
        .map((entry) => {
            const match = entry.match(/^(.*)\s×(\d+)$/);
            return match ? { name: match[1], quantity: parseInt(match[2], 10) } : { name: entry, quantity: 1 };
        })
        .filter((p) => p.name);
}

interface BundleSelectionListProps {
    parts: BundlePartLine[];
    className?: string;
}

/**
 * Collapsible list of the parts a customer picked for a Pick-Pack bundle.
 * Shared by the cart and both order-detail pages so bundle contents render
 * the same way everywhere instead of a single long inline badge.
 */
export function BundleSelectionList({ parts, className }: BundleSelectionListProps) {
    const [open, setOpen] = useState(false);
    if (!parts.length) return null;
    const count = parts.reduce((sum, p) => sum + p.quantity, 0);

    return (
        <div className={className}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
            >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
                Zawartość zestawu ({count} {count === 1 ? 'część' : 'części'})
            </button>
            {open && (
                <ul className="mt-1.5 space-y-0.5 pl-4.5">
                    {parts.map((p, i) => (
                        <li key={i} className="text-xs text-muted-foreground">
                            {p.name} <span className="font-semibold">×{p.quantity}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
